import type { TFunction } from 'i18next'

import { countryFlag } from './country'
import { REGION_FLAGS, regionByBit } from './regions'

/**
 * Page types and the kinds of place a Business page's location can be,
 * mirroring the backend's PageKind / LocationType enums (values are the wire
 * ints). Also the shared "country or region" label used wherever a page,
 * community or post shows the audience it was published to.
 */

/**
 * What a page stands for: a Topic (a brand, a creator, an idea) or a
 * Business (a company with locations people can walk into). Mirrors
 * Common.Enums.Pages.PageKind; the retired Location kind (10) is gone.
 */
export const PAGE_KIND = { topic: 0, business: 20 } as const
export type PageKind = (typeof PAGE_KIND)[keyof typeof PAGE_KIND]

export function isBusinessPage(page: { kind?: number | null }): boolean {
  return page.kind === PAGE_KIND.business
}

/**
 * The groups the kinds of place are filed under, in display order: the page
 * form lists the kinds in one section per group and the Map filters page
 * pins by group. Mirrors Common.Enums.Pages.LocationGroup.
 */
export const LOCATION_GROUPS = [
  { value: 1, key: 'foodDrink', emoji: '🍽️' },
  { value: 2, key: 'accommodation', emoji: '🏨' },
  { value: 3, key: 'shopping', emoji: '🛍️' },
  { value: 4, key: 'entertainmentCulture', emoji: '🎭' },
  { value: 5, key: 'natureOutdoor', emoji: '🌳' },
  { value: 6, key: 'sportsFitness', emoji: '🏅' },
  { value: 7, key: 'transportTravel', emoji: '✈️' },
  { value: 8, key: 'automotive', emoji: '🚗' },
  { value: 9, key: 'healthWellness', emoji: '🩺' },
  { value: 10, key: 'services', emoji: '🛎️' },
  { value: 11, key: 'education', emoji: '🎓' },
  { value: 12, key: 'governmentCommunity', emoji: '🏛️' },
] as const

export type LocationGroupInfo = (typeof LOCATION_GROUPS)[number]
export type LocationGroupKey = LocationGroupInfo['key']

/**
 * The kind of place a page location is, in display order (never sorted by
 * name). A kind's value is its group x 1000 + n, so the group is value / 1000.
 * Mirrors Common.Enums.Pages.LocationType; the retired kinds older apps send
 * are mapped by the backend, and there is no "Other".
 */
export const LOCATION_TYPES = [
  { value: 1001, group: 1, key: 'restaurant', emoji: '🍽️' },
  { value: 1002, group: 1, key: 'fastFood', emoji: '🍔' },
  { value: 1003, group: 1, key: 'cafe', emoji: '🥐' },
  { value: 1004, group: 1, key: 'coffeeShop', emoji: '☕' },
  { value: 1005, group: 1, key: 'bakery', emoji: '🥖' },
  { value: 1006, group: 1, key: 'iceCreamShop', emoji: '🍦' },
  { value: 1007, group: 1, key: 'pizzeria', emoji: '🍕' },
  { value: 1008, group: 1, key: 'foodCourt', emoji: '🍱' },
  { value: 1009, group: 1, key: 'bar', emoji: '🍸' },
  { value: 1010, group: 1, key: 'pub', emoji: '🍺' },
  { value: 1011, group: 1, key: 'brewery', emoji: '🍻' },
  { value: 1012, group: 1, key: 'winery', emoji: '🍷' },
  { value: 1013, group: 1, key: 'teaHouse', emoji: '🍵' },
  { value: 2001, group: 2, key: 'hotel', emoji: '🏨' },
  { value: 2002, group: 2, key: 'motel', emoji: '🛏️' },
  { value: 2003, group: 2, key: 'bedAndBreakfast', emoji: '🍳' },
  { value: 2004, group: 2, key: 'resort', emoji: '🏝️' },
  { value: 2005, group: 2, key: 'mountainHouse', emoji: '🛖' },
  { value: 3001, group: 3, key: 'supermarket', emoji: '🛒' },
  { value: 3002, group: 3, key: 'groceryStore', emoji: '🥬' },
  { value: 3003, group: 3, key: 'shoppingMall', emoji: '🏬' },
  { value: 3004, group: 3, key: 'clothingStore', emoji: '👗' },
  { value: 3005, group: 3, key: 'shoeStore', emoji: '👟' },
  { value: 3006, group: 3, key: 'electronicsStore', emoji: '📱' },
  { value: 3007, group: 3, key: 'bookstore', emoji: '📚' },
  { value: 3008, group: 3, key: 'toyStore', emoji: '🧸' },
  { value: 3009, group: 3, key: 'flowerShop', emoji: '💐' },
  { value: 3010, group: 3, key: 'furnitureStore', emoji: '🛋️' },
  { value: 3011, group: 3, key: 'hardwareStore', emoji: '🔨' },
  { value: 3012, group: 3, key: 'sportsStore', emoji: '🎽' },
  { value: 3013, group: 3, key: 'petStore', emoji: '🐾' },
  { value: 3014, group: 3, key: 'secondhandStore', emoji: '♻️' },
  { value: 4001, group: 4, key: 'cinema', emoji: '🎬' },
  { value: 4002, group: 4, key: 'theater', emoji: '🎭' },
  { value: 4003, group: 4, key: 'concertHall', emoji: '🎼' },
  { value: 4004, group: 4, key: 'museum', emoji: '🏛️' },
  { value: 4005, group: 4, key: 'artGallery', emoji: '🖼️' },
  { value: 4006, group: 4, key: 'library', emoji: '📖' },
  { value: 4007, group: 4, key: 'nightclub', emoji: '🪩' },
  { value: 4008, group: 4, key: 'casino', emoji: '🎰' },
  { value: 4009, group: 4, key: 'escapeRoom', emoji: '🗝️' },
  { value: 4010, group: 4, key: 'amusementPark', emoji: '🎢' },
  { value: 4011, group: 4, key: 'waterPark', emoji: '🌊' },
  { value: 4012, group: 4, key: 'zoo', emoji: '🦁' },
  { value: 4013, group: 4, key: 'aquarium', emoji: '🐠' },
  { value: 5001, group: 5, key: 'park', emoji: '🌳' },
  { value: 5002, group: 5, key: 'garden', emoji: '🌷' },
  { value: 5003, group: 5, key: 'botanicalGarden', emoji: '🌺' },
  { value: 5004, group: 5, key: 'natureReserve', emoji: '🦌' },
  { value: 5005, group: 5, key: 'nationalPark', emoji: '🏞️' },
  { value: 5006, group: 5, key: 'picnicArea', emoji: '🧺' },
  { value: 5007, group: 5, key: 'barbecueArea', emoji: '🍖' },
  { value: 5008, group: 5, key: 'fishingSpot', emoji: '🎣' },
  { value: 6001, group: 6, key: 'gym', emoji: '🏋️' },
  { value: 6002, group: 6, key: 'stadium', emoji: '🏟️' },
  { value: 6003, group: 6, key: 'footballField', emoji: '⚽' },
  { value: 6004, group: 6, key: 'basketballCourt', emoji: '🏀' },
  { value: 6005, group: 6, key: 'tennisCourt', emoji: '🎾' },
  { value: 6006, group: 6, key: 'volleyballCourt', emoji: '🏐' },
  { value: 6007, group: 6, key: 'swimmingPool', emoji: '🏊' },
  { value: 6008, group: 6, key: 'golfCourse', emoji: '⛳' },
  { value: 6009, group: 6, key: 'skiResort', emoji: '⛷️' },
  { value: 6010, group: 6, key: 'horseRidingCenter', emoji: '🐎' },
  { value: 6011, group: 6, key: 'runningTrack', emoji: '🏃' },
  { value: 7001, group: 7, key: 'airport', emoji: '✈️' },
  { value: 7002, group: 7, key: 'trainStation', emoji: '🚆' },
  { value: 7003, group: 7, key: 'busStation', emoji: '🚌' },
  { value: 7004, group: 7, key: 'metroStation', emoji: '🚇' },
  { value: 7005, group: 7, key: 'taxiStand', emoji: '🚕' },
  { value: 7006, group: 7, key: 'ferryTerminal', emoji: '⛴️' },
  { value: 7007, group: 7, key: 'carRental', emoji: '🚙' },
  { value: 7008, group: 7, key: 'parkingLot', emoji: '🅿️' },
  { value: 7009, group: 7, key: 'touristInfo', emoji: 'ℹ️' },
  { value: 8001, group: 8, key: 'gasStation', emoji: '⛽' },
  { value: 8002, group: 8, key: 'evCharging', emoji: '🔌' },
  { value: 8003, group: 8, key: 'carWash', emoji: '🧽' },
  { value: 8004, group: 8, key: 'autoRepair', emoji: '🔧' },
  { value: 8005, group: 8, key: 'tireShop', emoji: '🛞' },
  { value: 8006, group: 8, key: 'carDealership', emoji: '🚘' },
  { value: 8007, group: 8, key: 'autoParts', emoji: '⚙️' },
  { value: 8008, group: 8, key: 'vehicleInspection', emoji: '🔍' },
  { value: 8009, group: 8, key: 'motorcycleRepair', emoji: '🏍️' },
  { value: 8010, group: 8, key: 'restArea', emoji: '🛣️' },
  { value: 9001, group: 9, key: 'hospital', emoji: '🏥' },
  { value: 9002, group: 9, key: 'medicalClinic', emoji: '🩺' },
  { value: 9003, group: 9, key: 'pharmacy', emoji: '💊' },
  { value: 9004, group: 9, key: 'dentalClinic', emoji: '🦷' },
  { value: 9005, group: 9, key: 'eyeClinic', emoji: '👁️' },
  { value: 9006, group: 9, key: 'optician', emoji: '👓' },
  { value: 9007, group: 9, key: 'spa', emoji: '🧖' },
  { value: 9008, group: 9, key: 'massageCenter', emoji: '💆' },
  { value: 9009, group: 9, key: 'sauna', emoji: '♨️' },
  { value: 10001, group: 10, key: 'bank', emoji: '🏦' },
  { value: 10002, group: 10, key: 'atm', emoji: '🏧' },
  { value: 10003, group: 10, key: 'currencyExchange', emoji: '💱' },
  { value: 10004, group: 10, key: 'postOffice', emoji: '🏤' },
  { value: 10005, group: 10, key: 'courierOffice', emoji: '📦' },
  { value: 10006, group: 10, key: 'parcelLocker', emoji: '📬' },
  { value: 10007, group: 10, key: 'hairSalon', emoji: '💇' },
  { value: 10008, group: 10, key: 'barberShop', emoji: '💈' },
  { value: 10009, group: 10, key: 'beautySalon', emoji: '💄' },
  { value: 10010, group: 10, key: 'nailSalon', emoji: '💅' },
  { value: 10011, group: 10, key: 'tattooStudio', emoji: '🖋️' },
  { value: 10012, group: 10, key: 'laundry', emoji: '🧼' },
  { value: 10013, group: 10, key: 'dryCleaner', emoji: '👔' },
  { value: 10014, group: 10, key: 'travelAgency', emoji: '🧳' },
  { value: 10015, group: 10, key: 'realEstateAgency', emoji: '🏠' },
  { value: 11001, group: 11, key: 'kindergarten', emoji: '🖍️' },
  { value: 11002, group: 11, key: 'primarySchool', emoji: '🎒' },
  { value: 11003, group: 11, key: 'secondarySchool', emoji: '📐' },
  { value: 11004, group: 11, key: 'highSchool', emoji: '🏫' },
  { value: 11005, group: 11, key: 'college', emoji: '📓' },
  { value: 11006, group: 11, key: 'university', emoji: '🎓' },
  { value: 11007, group: 11, key: 'archaeologicalSite', emoji: '🏺' },
  { value: 12001, group: 12, key: 'cityHall', emoji: '🏢' },
  { value: 12002, group: 12, key: 'governmentOffice', emoji: '🗂️' },
  { value: 12003, group: 12, key: 'courthouse', emoji: '⚖️' },
  { value: 12004, group: 12, key: 'policeStation', emoji: '🚓' },
  { value: 12005, group: 12, key: 'fireStation', emoji: '🚒' },
  { value: 12006, group: 12, key: 'embassy', emoji: '🛂' },
  { value: 12007, group: 12, key: 'consulate', emoji: '🪪' },
  { value: 12008, group: 12, key: 'castle', emoji: '🏰' },
  { value: 12009, group: 12, key: 'palace', emoji: '👑' },
] as const

export type LocationTypeInfo = (typeof LOCATION_TYPES)[number]
export type LocationTypeKey = LocationTypeInfo['key']

const TYPE_BY_VALUE = new Map<number, LocationTypeInfo>(
  LOCATION_TYPES.map((type) => [type.value, type]),
)

/**
 * The kind of place for a wire value. Null, a cleared old value or anything
 * this build does not know is no kind at all (no chip, no pin badge).
 */
export function locationTypeByValue(
  value: number | null | undefined,
): LocationTypeInfo | undefined {
  return value == null ? undefined : TYPE_BY_VALUE.get(value)
}

/** Every group with its kinds of place, both in display order (the page form's sections). */
export const LOCATION_TYPE_SECTIONS: ReadonlyArray<{
  group: LocationGroupInfo
  types: readonly LocationTypeInfo[]
}> = LOCATION_GROUPS.map((group) => ({
  group,
  types: LOCATION_TYPES.filter((type) => type.group === group.value),
}))

/** Localized country name for an alpha-2 code (the code itself when unknown). */
export function countryName(code: string, language: string): string {
  try {
    return new Intl.DisplayNames([language], { type: 'region' }).of(code.toUpperCase()) ?? code
  } catch {
    return code
  }
}

/**
 * The audience a page, community or post was published to: one country or
 * one region, rendered as "🇷🇴 Romania" / "🌍 Europe"; nothing at all reads
 * as International, which is what the backend does with it.
 */
export function audienceLabel(
  countryCode: string | null | undefined,
  region: number | null | undefined,
  tCommon: TFunction<'common'>,
  language: string,
  withEmoji = true,
): string {
  const info = region != null ? regionByBit(region) : undefined
  if (info) {
    const name = tCommon(`regions.${info.key}`)
    return withEmoji ? `${info.emoji} ${name}` : name
  }
  if (countryCode) {
    const name = countryName(countryCode, language)
    return withEmoji ? `${countryFlag(countryCode)} ${name}` : name
  }
  const international = REGION_FLAGS[0]
  const name = tCommon(`regions.${international.key}`)
  return withEmoji ? `${international.emoji} ${name}` : name
}

export function audienceEmoji(
  countryCode: string | null | undefined,
  region: number | null | undefined,
): string {
  const info = region != null ? regionByBit(region) : undefined
  if (info) return info.emoji
  if (countryCode) return countryFlag(countryCode)
  return REGION_FLAGS[0].emoji
}
